import {
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import type { DataImportInfo } from "../../../api/types/dataImportTypes";
import ImportDialogIcon from "@entrilia/shared/icons/ImportDialogIcon";
import type { StartDataImportOptions } from "./importDataPagesTypes";

interface Props {
  dataImport: DataImportInfo;
  errorsCount: number;
  deleteRecordsCount: number;
  onClose: () => void;
  onConfirm: (options: StartDataImportOptions) => void;
}

const StartImportConfirmationDialog = ({ dataImport, errorsCount, deleteRecordsCount, onClose, onConfirm }: Props) => {
  const [startOptions, setStartOptions] = useState<StartDataImportOptions>({
    sendEmailNotificationWhenFinished: dataImport.applicationOptions === "AppliedAsynchronouslyByExternalService",
  });

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Start Data Import</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ImportDialogIcon sx={{ width: 80, height: 80 }} />

          <Typography variant="h6">Start data import?</Typography>

          {deleteRecordsCount > 0 && (
            <Typography color="warning">
              Contacts marked Delete are removed from Entrilia and Fund Accounting. An import that deletes contacts
              cannot be rolled back.
            </Typography>
          )}

          {errorsCount > 0 && (
            <Typography color="warning">
              This data import contains {errorsCount} rows with errors. They will be excluded from the final import.
            </Typography>
          )}

          <Stack spacing={1}>
            {dataImport.applicationOptions === "AppliedAsynchronouslyByExternalService" && (
              <Typography color="textSecondary">
                This data import will be applied asynchronously by the system. The application process might take some
                time. To receive a notification regarding the completion of the application, please keep the box below
                checked.
              </Typography>
            )}

            <FormControlLabel
              label="Send notification email when finished"
              control={
                <Checkbox
                  checked={startOptions.sendEmailNotificationWhenFinished}
                  onChange={(_, checked) =>
                    setStartOptions((prev) => ({ ...prev, sendEmailNotificationWhenFinished: checked }))
                  }
                />
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogButtons onCancel={onClose} cancelText="Continue Editing">
        <Button variant="contained" onClick={() => onConfirm(startOptions)}>
          Start Import
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default StartImportConfirmationDialog;
